/**
 * State-specific onboarding steps for MMJ.com patients.
 * Used on the patient dashboard to guide new and renewal patients
 * through the post-appointment process for their state.
 *
 * card_type values: "new-card" | "renewal"
 * Each step has: title, description, link (optional), linkText (optional)
 *
 * IMPORTANT:
 * - Step 1 is ALWAYS about scheduling/completing the MMJ.com appointment
 * - All references are to "your MMJ.com doctor", never generic
 * - Custom flows exist for GA, MD, PA, VA (require emailing support@MMJ.com)
 * - Louisiana has no card/registry, doctor faxes to pharmacy
 * - Texas has no patient portal, doctor enters into CURT
 * - No em dashes anywhere
 */

const STATE_ONBOARDING_STEPS = {
  az: {
    name: 'Arizona',
    registrationUrl: 'https://individual-licensing.azdhs.gov/s/login/',
    fee: '$150 ($75 for SNAP recipients)',
    cardDuration: '2 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will provide the signed Physician Certification form needed for your state application.',
      },
      {
        title: 'Create Your ADHS Account',
        description:
          'Go to the Arizona Department of Health Services Individual Licensing Portal and click "Register" to create your account.',
        link: 'https://individual-licensing.azdhs.gov/s/login/',
        linkText: 'ADHS Licensing Portal',
      },
      {
        title: 'Submit Your Patient Application',
        description:
          'Log in and click "Add a New License," then select "Marijuana" and "New Patient Application." Upload your Arizona driver\'s license or state ID, a passport-style photo (white background, taken within 60 days), and the signed Physician Certification from your MMJ.com doctor.',
      },
      {
        title: 'Pay the State Fee',
        description:
          'Pay the $150 application fee online. If you receive SNAP benefits, upload your documentation to qualify for the reduced $75 fee.',
      },
      {
        title: 'Download Your Digital Card',
        description:
          'Arizona processes applications in 5 to 10 business days. Once approved, download your electronic medical marijuana card from the portal. Arizona issues digital cards only, no physical card is mailed. Medical patients save approximately 16% in taxes compared to recreational purchases.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. You can begin the renewal process up to 90 days before your card expires.',
      },
      {
        title: 'Submit Your Renewal Application',
        description:
          'Log in to the ADHS portal and submit a renewal application with your updated Physician Certification. Pay the $150 renewal fee ($75 for SNAP recipients).',
        link: 'https://individual-licensing.azdhs.gov/s/login/',
        linkText: 'ADHS Licensing Portal',
      },
      {
        title: 'Download Your Updated Card',
        description:
          'Once processed, download your renewed digital card from the portal. If your card has already expired, a full new application is required instead of a renewal.',
      },
    ],
  },

  ar: {
    name: 'Arkansas',
    registrationUrl: 'https://mmj.adh.arkansas.gov/',
    fee: '$50 (no reduced fee available)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will provide the physician certification required for your state application.',
      },
      {
        title: 'Register on the Arkansas MMJ Portal',
        description:
          'Go to the Arkansas Medical Marijuana portal, click "Patient Registration," and create your account. Upload your Arkansas driver\'s license or state ID and the physician certification from your MMJ.com appointment.',
        link: 'https://mmj.adh.arkansas.gov/',
        linkText: 'Arkansas MMJ Portal',
      },
      {
        title: 'Pay the $50 Application Fee',
        description:
          'Pay the $50 non-refundable fee online (Visa, MasterCard, or Discover accepted). Processing takes up to 14 business days.',
      },
      {
        title: 'Print Your Patient Card',
        description:
          'Once approved, log in and print your medical marijuana card from your online account. Bring it along with your photo ID to any licensed Arkansas dispensary.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor at least 30 days before your card expires.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the Arkansas MMJ portal and submit a renewal application with your updated certification. Pay the $50 renewal fee.',
        link: 'https://mmj.adh.arkansas.gov/',
        linkText: 'Arkansas MMJ Portal',
      },
      {
        title: 'Print Your Updated Card',
        description: 'Once processed, log in and print your renewed card.',
      },
    ],
  },

  ct: {
    name: 'Connecticut',
    registrationUrl: 'https://biznet.ct.gov/dcp-mmrp',
    fee: '$0 (free)',
    cardDuration: '1 year (set by provider)',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. Make sure to provide your email address and phone number during the appointment, as your doctor needs these to initiate your state registration.',
      },
      {
        title: 'Check Your Email',
        description:
          'After your MMJ.com doctor enters your certification into the state system, you will receive an email notification. This is your signal to complete the registration.',
      },
      {
        title: 'Create Your BizNet Account and Register',
        description:
          'Go to the Connecticut DCP Medical Marijuana Registration page and create an account on BizNet. Verify your email, log in, select "I Am a Patient," confirm your information, and upload your proof of identity and Connecticut residency.',
        link: 'https://portal.ct.gov/cannabis/knowledge-base/articles/mmp/register-for-a-medical-marijuana-card?language=en_US',
        linkText: 'CT Registration Instructions',
      },
      {
        title: 'Receive Your Card',
        description:
          'There is no state fee. Connecticut registration is completely free. Your physical card will be mailed after the Department reviews your application. Medical patients in Connecticut purchase cannabis tax-free, saving approximately 14% compared to recreational prices.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. You can begin 30 days before your current certification expires.',
      },
      {
        title: 'Complete Your Renewal',
        description:
          'Log in to the CT medical marijuana portal and submit your renewal application with any updated documents. There is no renewal fee.',
        link: 'https://portal.ct.gov/cannabis/medical-marijuana-program?language=en_US',
        linkText: 'CT Medical Marijuana Program',
      },
      {
        title: 'Receive Your Updated Card',
        description:
          'Your renewed card will be mailed after processing. Continue enjoying tax-free medical purchases.',
      },
    ],
  },

  fl: {
    name: 'Florida',
    registrationUrl: 'https://mmuregistry.flhealth.gov/',
    fee: '$75 (no reduced fee)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your evaluation with an MMJ.com doctor. Note: Florida law requires the first visit to be in-person. Your MMJ.com doctor will enter your information into the state Medical Marijuana Use Registry (MMUR) if approved.',
      },
      {
        title: 'Check Your Email for Login Credentials',
        description:
          'After your appointment, you will receive two emails from the MMUR: one with your username and one with a temporary password.',
      },
      {
        title: 'Complete Your MMUR Registration',
        description:
          'Log in to the MMUR, change your temporary password, navigate to "Your Card," and complete the application. Upload your Florida driver\'s license or state ID, a passport-style color photo, and proof of residency.',
        link: 'https://mmuregistry.flhealth.gov/',
        linkText: 'Florida MMUR',
      },
      {
        title: 'Pay the $75 State Fee',
        description:
          'Pay the $75 processing fee online. Your approval email serves as temporary authorization while you wait for your physical card, which arrives via USPS in 2 to 3 weeks.',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Bring your medical card (or approval email) and photo ID to any licensed Florida dispensary.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. Telehealth is allowed for renewals.',
      },
      {
        title: 'Renew on the MMUR',
        description:
          'Log in to the MMUR beginning 45 days before your card expires (not earlier). Submit your renewal application and pay the $75 fee.',
        link: 'https://mmuregistry.flhealth.gov/',
        linkText: 'Florida MMUR',
      },
      {
        title: 'Receive Your Updated Card',
        description:
          'Your approval email serves as temporary authorization. Your new physical card will arrive via USPS.',
      },
    ],
  },

  ga: {
    name: 'Georgia',
    registrationUrl: null,
    fee: '$30 (+ $3.75 online service fee)',
    cardDuration: '5 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          "Book your telehealth evaluation with an MMJ.com doctor for Georgia's Low THC Oil program.",
      },
      {
        title: 'Email Your ID to MMJ.com',
        description:
          "Email a clear photo of the front AND back of your valid driver's license or state ID to support@MMJ.com. Your MMJ.com doctor needs this to process your registration.",
      },
      {
        title: 'Your Doctor Submits Your Registration',
        description:
          'Your MMJ.com doctor will enter your information into the Georgia Department of Public Health Low THC Oil Registry on your behalf. You do not need to register yourself.',
      },
      {
        title: 'Pay the State Fee',
        description:
          'After approval, you will receive an email with a link to the payment portal. Pay the $30 fee (+ $3.75 service fee) within 60 days. You can also mail a $30 money order.',
      },
      {
        title: 'Receive Your Card',
        description:
          "Your physical card will be shipped via UPS with signature required. Allow up to 10 business days after payment. Georgia cards are valid for 5 years. Georgia's program permits low-THC oil only (5% THC or less).",
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. Since Georgia cards last 5 years, renewals are infrequent.',
      },
      {
        title: 'Email Your Updated ID',
        description:
          'Email a clear photo of the front and back of your current valid ID to support@MMJ.com.',
      },
      {
        title: 'Pay for Your New Card',
        description:
          'Pay the $30 fee (+ $3.75 service fee) when you receive the payment email. Your new card will be shipped via UPS.',
      },
    ],
  },

  il: {
    name: 'Illinois',
    registrationUrl:
      'https://dph.illinois.gov/topics-services/prevention-wellness/medical-cannabis.html',
    fee: '$50/1yr, $100/2yr, $125/3yr ($25/$50/$75 for Veterans, SSI, SSDI)',
    cardDuration: '1, 2, or 3 years (your choice)',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will submit your certification to the Illinois system.',
      },
      {
        title: 'Register on the Illinois Medical Cannabis Portal',
        description:
          'Visit the Illinois Department of Public Health Medical Cannabis page and click "Medical Cannabis Patient Program" or "Opioid Alternative Patient Program" to begin your registration.',
        link: 'https://dph.illinois.gov/topics-services/prevention-wellness/medical-cannabis.html',
        linkText: 'Illinois Medical Cannabis',
      },
      {
        title: 'Complete Your Application',
        description:
          'Upload a passport-style photo, your Illinois state ID, proof of residency, and any veteran/disability documentation if you qualify for reduced fees. Choose your card duration: 1 year ($50), 2 years ($100), or 3 years ($125). Veterans and SSI/SSDI recipients pay $25/$50/$75.',
      },
      {
        title: 'Print Your Temporary Card',
        description:
          'If submitted before 6:00 PM CST, you can print a provisional (temporary) card the next day after 8:30 PM. Your permanent card will be issued after full approval (4 to 6 weeks). Medical patients in Illinois save approximately 30% or more in taxes compared to recreational purchases, and can grow up to 5 plants at home.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description: 'Book your renewal evaluation with your MMJ.com doctor.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the Illinois Medical Cannabis Patient Portal and submit your renewal application with updated documents. Pay the renewal fee for your chosen card duration.',
        link: 'https://dph.illinois.gov/topics-services/prevention-wellness/medical-cannabis.html',
        linkText: 'Illinois Medical Cannabis',
      },
      {
        title: 'Print Your Updated Card',
        description:
          'Your renewed card will be available after processing. Continue enjoying significant tax savings and home grow privileges.',
      },
    ],
  },

  ia: {
    name: 'Iowa',
    registrationUrl: 'https://idph.my.salesforce-sites.com/IowaReg',
    fee: '$100 ($25 for SSI, SSDI, Medicaid, HAWK-I)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will provide the signed Healthcare Practitioner Certification Form (HPCF).',
      },
      {
        title: 'Register Online',
        description:
          'Go to the Iowa Medical Cannabis patient registration page. Select "New Patient" and complete the Adult Patient Application. Upload your passport-style photo, signed HPCF, and Iowa driver\'s license or state ID. You must submit within 60 days of your physician certification.',
        link: 'https://idph.my.salesforce-sites.com/IowaReg',
        linkText: 'Iowa Patient Registration',
      },
      {
        title: 'Pay the State Fee',
        description:
          'Pay the $100 application fee online. If you receive SSI, SSDI, Medicaid, or HAWK-I benefits, upload your documentation to qualify for the reduced $25 fee.',
      },
      {
        title: 'Receive Your Card',
        description:
          'A temporary digital card will be emailed immediately. Your permanent card will be mailed to your address. Iowa permits capsules, tinctures, topicals, and vaporization products only. The THC purchase limit is 4.5 grams per 90-day period.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. You will receive a reminder 60 days before expiration.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Visit the registration portal, select "Yes, I am a renewing patient," enter your registration number and email, upload your updated documents, and pay the renewal fee.',
        link: 'https://idph.my.salesforce-sites.com/IowaReg',
        linkText: 'Iowa Patient Registration',
      },
      {
        title: 'Receive Your Updated Card',
        description: 'Your renewed card will be mailed after processing.',
      },
    ],
  },

  la: {
    name: 'Louisiana',
    registrationUrl: null,
    fee: '$0 (no state fee)',
    cardDuration: 'Up to 12 months',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. During your appointment, let your doctor know which licensed medical cannabis pharmacy you would like to be enrolled at.',
      },
      {
        title: 'Your Doctor Faxes the Recommendation',
        description:
          'If approved, your MMJ.com doctor will fax the recommendation form directly to the pharmacy you selected. There is no state registration, no application, and no card required in Louisiana.',
      },
      {
        title: 'The Pharmacy Will Call You',
        description:
          'The pharmacy will contact you to complete your enrollment. If the pharmacy does not call you within 48 hours, please contact the pharmacy directly and ask them to look for your recommendation. It is common for pharmacies to have trouble locating faxed recommendations, so do not worry if this happens. If the pharmacy says they cannot locate it, email support@MMJ.com and request the fax to be resubmitted. Once enrolled, bring a valid photo ID to the pharmacy to make your first purchase.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your current recommendation expires. Let your doctor know which pharmacy you want the renewal sent to.',
      },
      {
        title: 'Your Doctor Faxes the Updated Recommendation',
        description:
          'Your MMJ.com doctor will fax the renewed recommendation to your pharmacy. The pharmacy will call you to confirm.',
      },
      {
        title: 'Continue Your Pharmacy Visits',
        description:
          'Bring your photo ID when purchasing. No additional registration or fees are required.',
      },
    ],
  },

  md: {
    name: 'Maryland',
    registrationUrl:
      'https://onestop.md.gov/public_profiles/adult-patient-registration-601c0fd9f9d7557af267e1e1',
    fee: '$0 (free registration). Optional $25 physical card (waived for VA enrollees and Medicaid)',
    cardDuration: '6 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          "Book your telehealth evaluation with an MMJ.com doctor. This is your first step to getting access to Maryland's medical cannabis program.",
      },
      {
        title: 'Register for Your MMCC Number',
        description:
          'If approved, visit the Maryland OneStop Portal to register as a medical cannabis patient. Complete the application in a single session (it cannot be saved and resumed). Upload your photo ID, a passport-style photo, and proof of Maryland residency. Registration is free. You will receive a 16-digit MMCC patient number.',
        link: 'https://onestop.md.gov/public_profiles/adult-patient-registration-601c0fd9f9d7557af267e1e1',
        linkText: 'Maryland OneStop Portal',
      },
      {
        title: 'Email Your MMCC Number to MMJ.com',
        description:
          'Once you receive your 16-digit MMCC patient number, email it to support@MMJ.com. Your MMJ.com doctor needs this number to activate and submit your 12-month prescription to your account, which allows you to purchase medical cannabis.',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Once your doctor activates your prescription, you can visit any licensed Maryland dispensary with your patient ID and photo ID. Medical patients in Maryland pay 0% cannabis tax (recreational is taxed at 9%). Registration lasts 6 years.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. Maryland requires annual physician recertification even though your patient registration lasts 6 years.',
      },
      {
        title: 'Email Your MMCC Number to MMJ.com',
        description:
          'Email your 16-digit MMCC number to support@MMJ.com so your doctor can submit your renewed 12-month prescription.',
      },
      {
        title: 'Continue Dispensary Access',
        description:
          'Once your doctor activates your renewed prescription, continue visiting any licensed Maryland dispensary with 0% tax on medical purchases.',
      },
    ],
  },

  mi: {
    name: 'Michigan',
    registrationUrl: 'https://aca-prod.accela.com/MIMM/Default.aspx',
    fee: '$40 (no reduced fee)',
    cardDuration: '2 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will provide the physician certification needed for your state application.',
      },
      {
        title: 'Register on the LARA Portal',
        description:
          'Go to the Michigan LARA portal and create an account. Select "Registry Cards," then "Patient Only Registry Application." Enter your personal information and upload your physician certification and proof of Michigan residency.',
        link: 'https://aca-prod.accela.com/MIMM/Default.aspx',
        linkText: 'Michigan LARA Portal',
      },
      {
        title: 'Pay the $40 State Fee',
        description:
          'Pay the $40 fee online. Processing takes up to 15 business days. Your approval email serves as a temporary card for immediate dispensary access while you wait for the physical card.',
      },
      {
        title: 'Receive Your Card',
        description:
          'Your physical card will be mailed via USPS. Medical patients in Michigan save approximately 34% in taxes compared to recreational purchases and can grow up to 12 plants.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. You can begin 90 days before your card expires.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the LARA portal, select "Registry Cards," then "Renew Application." Upload your updated certification and pay the $40 renewal fee.',
        link: 'https://aca-prod.accela.com/MIMM/Default.aspx',
        linkText: 'Michigan LARA Portal',
      },
      {
        title: 'Receive Your Updated Card',
        description: 'Your renewed card will be mailed after processing.',
      },
    ],
  },

  mn: {
    name: 'Minnesota',
    registrationUrl: 'https://cannabis.web.health.state.mn.us/',
    fee: '$0 (free)',
    cardDuration: '3 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will submit your certification electronically to the Minnesota Medical Cannabis Registry.',
      },
      {
        title: 'Check Your Email',
        description:
          'You will receive an email from the Office of Cannabis Management with a unique enrollment link. Click the link and create your account using the same email address you provided to your MMJ.com doctor.',
      },
      {
        title: 'Complete Your Enrollment',
        description:
          'Fill out the online enrollment application with your personal details and upload your government-issued photo ID. There is no fee. The OCM processes applications within 30 days.',
        link: 'https://cannabis.web.health.state.mn.us/',
        linkText: 'Minnesota Medical Cannabis Registry',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Minnesota does not issue a physical card. Use your registry account plus your government ID at the dispensary. Medical cannabis in Minnesota is 100% tax-free. Your registration is valid for 3 years. Veterans can self-certify without a physician visit.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. Start 60 to 90 days before your registration expires. Minnesota certifications are valid for 3 years.',
      },
      {
        title: 'Complete Your Re-Enrollment',
        description:
          'You will receive a new email from the OCM. Log in and complete the re-enrollment application. No fee required.',
        link: 'https://cannabis.web.health.state.mn.us/',
        linkText: 'Minnesota Medical Cannabis Registry',
      },
      {
        title: 'Continue Tax-Free Dispensary Access',
        description:
          'Continue visiting any licensed dispensary with your registry account and government ID. Medical purchases remain 100% tax-free.',
      },
    ],
  },

  mo: {
    name: 'Missouri',
    registrationUrl: 'https://mo-public.mycomplia.com/',
    fee: '~$28 (adjusted annually by CPI)',
    cardDuration: '3 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will submit your electronic certification to the Missouri system.',
      },
      {
        title: 'Register on the Missouri Complia Portal',
        description:
          'Go to the Missouri patient portal, click "Register," and create your account. Click "+ Create New Application," select "Patient," then "New Patient Registration." Link your doctor\'s electronic certification and upload your photo ID and a clear color photo. You must submit within 30 days of your certification date.',
        link: 'https://mo-public.mycomplia.com/',
        linkText: 'Missouri Patient Portal',
      },
      {
        title: 'Pay the State Fee',
        description:
          'Pay the approximately $28 application fee (adjusted annually). The state has 30 days to process your application.',
      },
      {
        title: 'Download Your Digital Card',
        description:
          'Once approved, download your digital ID card from the portal. Missouri cards are valid for 3 years. Medical patients pay a lower tax rate than recreational buyers and can grow plants at home with a separate cultivation card (~$55).',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor 30 to 60 days before expiration.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the Complia portal and select "+ Create New Application," then "Patient Renewal." Upload your new certification and pay the renewal fee.',
        link: 'https://mo-public.mycomplia.com/',
        linkText: 'Missouri Patient Portal',
      },
      {
        title: 'Download Your Updated Card',
        description: 'Download your renewed digital card after processing.',
      },
    ],
  },

  mt: {
    name: 'Montana',
    registrationUrl: 'https://tap.dor.mt.gov/_/',
    fee: '$20 (no reduced fee)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will provide the physician statement required for your state application.',
      },
      {
        title: 'Register on the Montana TAP Portal',
        description:
          'Go to the Montana TransAction Portal, create an account, scroll to "Cannabis Control," and click "Apply for or Manage a Medical Marijuana Card." Upload your physician statement, Montana driver\'s license or state ID, and a passport-style photo.',
        link: 'https://tap.dor.mt.gov/_/',
        linkText: 'Montana TAP Portal',
      },
      {
        title: 'Pay the $20 Fee and Print Your Temporary Card',
        description:
          'Pay the $20 fee online and immediately print a temporary card. You must physically print it (digital display is not valid at dispensaries). The temporary card is valid for up to 60 days while your application processes.',
      },
      {
        title: 'Receive Your Permanent Card',
        description:
          'Your permanent card will be mailed after approval (7 to 30 days). Medical patients in Montana save approximately 16% in taxes compared to recreational purchases and can access higher-potency products.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor at least 30 days before your card expires.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the TAP portal and select "Renew an Existing Card." Upload your updated documents and pay the $20 renewal fee. Print your temporary card immediately.',
        link: 'https://tap.dor.mt.gov/_/',
        linkText: 'Montana TAP Portal',
      },
      {
        title: 'Receive Your Updated Card',
        description:
          'Your renewed permanent card will be mailed after processing.',
      },
    ],
  },

  nj: {
    name: 'New Jersey',
    registrationUrl:
      'https://njmcp.crc.nj.gov/web/#/home/createAdultPatientUser',
    fee: '$0 for digital card ($10 for optional physical card)',
    cardDuration: '2 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will issue the Authorizing Healthcare Practitioner Statement containing the reference ID and registry ID numbers you need to register.',
      },
      {
        title: 'Register on the NJ Medicinal Cannabis Portal',
        description:
          'Go to the New Jersey Medicinal Cannabis portal and select "Adult Patient Registration." Enter your registry ID and reference number exactly as they appear on your certification. Verify your account via email and set a password.',
        link: 'https://njmcp.crc.nj.gov/web/#/home/createAdultPatientUser',
        linkText: 'NJ Medicinal Cannabis Portal',
      },
      {
        title: 'Complete Your Application',
        description:
          'Confirm your information, choose a dispensary (Alternative Treatment Center), and upload a full-face photo (white background, taken within 60 days) and your NJ government-issued ID or two proofs of residency. The digital card is free. A physical card is $10 if you want one.',
      },
      {
        title: 'Receive Your Digital Card',
        description:
          'The state reviews applications within 30 days. Your digital card is available immediately upon approval. Medical cannabis in New Jersey is exempt from the 6.625% sales tax that recreational buyers pay.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor. You will receive reminder emails 60 and 30 days before expiration.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the NJ portal and upload your new certification, updated photo, and current ID. Select free digital card or pay $10 for a physical card.',
        link: 'https://njmcp.crc.nj.gov/web/#/home/createAdultPatientUser',
        linkText: 'NJ Medicinal Cannabis Portal',
      },
      {
        title: 'Receive Your Updated Card',
        description:
          'Your renewed digital card will be available upon approval.',
      },
    ],
  },

  ny: {
    name: 'New York',
    registrationUrl: null,
    fee: '$0 (free, automatic registration)',
    cardDuration: 'Up to 1 year (set by provider)',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. New York has no qualifying condition list, so your doctor will determine if medical cannabis is right for you.',
      },
      {
        title: 'Your Doctor Emails Your Certification',
        description:
          'If approved, your MMJ.com doctor will email you a Patient Certification with a Registry ID number. You are automatically registered in the New York system. There is no state application, no portal to visit, and no fee. Check your spam folder if you do not receive it within 24 hours.',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Use your certification document and a government photo ID immediately at any licensed medical dispensary. Medical cannabis in New York is taxed at approximately 3.15% vs. 13% for recreational. Medical patients can also grow up to 6 plants at home.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your certification expires.',
      },
      {
        title: 'Automatic Re-Registration',
        description:
          'Your MMJ.com doctor issues a renewed certification. You are automatically re-registered. No application or fee required. Continue visiting dispensaries with your updated certification and photo ID.',
      },
    ],
  },

  oh: {
    name: 'Ohio',
    registrationUrl:
      'https://www.ohiomedicalmarijuanaregistry.com/logins/sign_in',
    fee: '$0 (free)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will create your profile and upload your certification directly into the Ohio Medical Marijuana Registry.',
      },
      {
        title: 'Check Your Email',
        description:
          'You will receive a unique activation email from the Ohio Medical Marijuana Control Program. This email is valid for 90 days and can only be used once. Check your spam folder if you do not see it within 24 hours.',
      },
      {
        title: 'Complete Your Registration',
        description:
          "Click the link in the email, set your password, and fill out the application with your personal information. You will need your Ohio driver's license or state-issued ID number. There is no fee.",
        link: 'https://www.ohiomedicalmarijuanaregistry.com/logins/sign_in',
        linkText: 'Ohio MMJ Registry',
      },
      {
        title: 'Download Your Digital Card',
        description:
          'Once approved (typically 1 to 2 business days), download your digital medical marijuana card from the registry. Ohio issues digital cards only. Medical patients are exempt from the 10% recreational excise tax, can possess up to 9 oz (vs. 2.5 oz recreational), and can access dispensaries at age 18.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor 30 to 45 days before expiration. Ohio does NOT send renewal reminders, so track your expiration date.',
      },
      {
        title: 'Renew on the Registry',
        description:
          'Log in to the Ohio Medical Marijuana Registry and click "Renew Card." No fee required.',
        link: 'https://www.ohiomedicalmarijuanaregistry.com/logins/sign_in',
        linkText: 'Ohio MMJ Registry',
      },
      {
        title: 'Download Your Updated Card',
        description: 'Download your renewed digital card after processing.',
      },
    ],
  },

  ok: {
    name: 'Oklahoma',
    registrationUrl: 'https://medportal.omma.ok.gov/',
    fee: '$104.30 ($22.50 for Medicaid/SoonerSelect, Medicare, or 100% disabled veterans)',
    cardDuration: '2 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. Oklahoma does not require a specific qualifying condition, so your doctor will determine if medical cannabis is right for you.',
      },
      {
        title: 'Register on the OMMA MedPortal',
        description:
          'Go to the OMMA MedPortal and create your account. Complete the online patient application. Upload the signed physician recommendation from your MMJ.com doctor, proof of Oklahoma residency, proof of identity, and a clear full-face color photo.',
        link: 'https://medportal.omma.ok.gov/',
        linkText: 'OMMA MedPortal',
      },
      {
        title: 'Pay the State Fee',
        description:
          'Pay the $104.30 fee online (Visa, MasterCard, or Discover). If you receive Medicaid/SoonerSelect, Medicare, or are a 100% disabled veteran, upload your documentation to qualify for the reduced $22.50 fee. OMMA processes within 14 business days.',
      },
      {
        title: 'Receive Your Card',
        description:
          'Your approval email or MedPortal approval can be used as temporary ID at dispensaries. Your physical card will be mailed via USPS. Oklahoma cards are valid for 2 years and patients can possess 3 oz on their person, 8 oz at home, and grow 6 mature plants.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description: 'Book your renewal evaluation with your MMJ.com doctor.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the MedPortal, go to "License Dashboard," and select "Patient Renewal." Upload your new recommendation and pay the same fee.',
        link: 'https://medportal.omma.ok.gov/',
        linkText: 'OMMA MedPortal',
      },
      {
        title: 'Receive Your Updated Card',
        description: 'Your renewed card will be mailed after processing.',
      },
    ],
  },

  pa: {
    name: 'Pennsylvania',
    registrationUrl: 'https://padohmmp.custhelp.com/app/login',
    fee: '$50 ($0 for SNAP, Medicaid, Medicare, CHIP, WIC, PACE/PACENET, SSI)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. This is your first step. If approved, your doctor will submit your certification to the Pennsylvania Medical Marijuana Program.',
      },
      {
        title: 'Register with the PA Department of Health',
        description:
          "After your MMJ.com doctor approves you, visit the PA Medical Marijuana Program Registry and create your patient profile. Use a desktop computer and make sure your name and address match your Pennsylvania driver's license or PennDOT ID exactly. You will receive a Patient ID number.",
        link: 'https://padohmmp.custhelp.com/app/login',
        linkText: 'PA MMJ Registry',
      },
      {
        title: 'Email Your Patient ID to MMJ.com',
        description:
          'Once you receive your Patient ID number from the PA registry, email it to support@MMJ.com. Your doctor needs this to complete the certification process in the state system.',
      },
      {
        title: 'Pay the State Fee',
        description:
          'Log in to the PA registry and pay the $50 annual fee. If you participate in SNAP, Medicaid, Medicare, CHIP, WIC, PACE/PACENET, or SSI, you qualify for a $0 fee waiver. Upload your proof of enrollment.',
      },
      {
        title: 'Receive Your Card in the Mail',
        description:
          'The Pennsylvania Department of Health will print and mail your medical marijuana ID card. This typically takes 5 to 7 business days. Bring your card and a valid photo ID to any licensed Pennsylvania dispensary.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your card expires.',
      },
      {
        title: 'Email Your Patient ID to MMJ.com',
        description:
          'Email your Patient ID number to support@MMJ.com so your doctor can submit the renewed certification.',
      },
      {
        title: 'Pay the Renewal Fee',
        description:
          'Log in to the PA registry, review your information, and pay the $50 fee (or $0 with fee waiver). Your new card will be mailed with an issue date the day after your old card expires.',
        link: 'https://padohmmp.custhelp.com/app/login',
        linkText: 'PA MMJ Registry',
      },
    ],
  },

  tx: {
    name: 'Texas',
    registrationUrl: null,
    fee: '$0 (no state fee)',
    cardDuration: '1 year (with four 90-day refills)',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your evaluation with an MMJ.com doctor. Texas operates under the Compassionate Use Program (CUP) with qualifying conditions including epilepsy, seizure disorders, MS, spasticity, ALS, autism, terminal cancer, PTSD, chronic pain, and incurable neurodegenerative diseases.',
      },
      {
        title: 'Your Doctor Handles Everything',
        description:
          'If approved, your MMJ.com doctor enters your prescription directly into the Compassionate Use Registry of Texas (CURT). There is no patient portal, no application, no card, and no state fee. You do not need to do anything.',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Bring a valid government-issued photo ID to a licensed Texas dispensary. The dispensary verifies your prescription in CURT using your name, date of birth, and last 5 digits of your SSN. Texas permits low-THC cannabis only (up to 10mg THC per dose).',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor when your prescription expires.',
      },
      {
        title: 'Your Doctor Updates CURT',
        description:
          'Your MMJ.com doctor enters a new prescription into CURT. No paperwork or fees required on your end. Continue visiting dispensaries with your photo ID.',
      },
    ],
  },

  vt: {
    name: 'Vermont',
    registrationUrl: 'https://ccb.vermont.gov/med-forms',
    fee: '$50 (no reduced fee)',
    cardDuration: '3 years',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will complete and submit the Healthcare Professional Verification Form (HCPVF) to the Vermont Cannabis Control Board.',
      },
      {
        title: 'Create Your Patient Account',
        description:
          'Visit the Vermont Cannabis Control Board forms page and click "Create an initial patient account." Use your email address as your username. Complete the application with your personal information, a current color photo, and your Vermont driver\'s license or non-driver ID number.',
        link: 'https://ccb.vermont.gov/med-forms',
        linkText: 'Vermont CCB Forms',
      },
      {
        title: 'Pay the $50 Fee',
        description:
          'Pay the $50 non-refundable fee by credit or debit card. The CCB has up to 30 days to review your application.',
      },
      {
        title: 'Receive Your Card',
        description:
          'Your registry card will be mailed to your address upon approval. Vermont cards are valid for 3 years. Medical purchases are completely tax-free (recreational is taxed approximately 20%), and medical patients can grow 6 mature plants plus 12 immature plants.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your card expires.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the Vermont portal and complete the renewal application with an updated photo. Pay the $50 renewal fee.',
        link: 'https://vtmedmj.visualvault.com/',
        linkText: 'Vermont Patient Portal',
      },
      {
        title: 'Receive Your Updated Card',
        description: 'Your renewed card will be mailed after processing.',
      },
    ],
  },

  va: {
    name: 'Virginia',
    registrationUrl: null,
    fee: '$0 (no mandatory state fee)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. Virginia has no qualifying condition list, so your doctor will determine if medical cannabis is appropriate for your situation.',
      },
      {
        title: 'Email Your ID to MMJ.com',
        description:
          "Email a clear photo of your valid Virginia driver's license, state ID, or temporary driver's license/state ID to support@MMJ.com. Your MMJ.com doctor needs this to submit your certification.",
      },
      {
        title: 'Your Doctor Emails Your Digital Card',
        description:
          'Once your MMJ.com doctor processes your certification, they will email your digital medical cannabis card directly to you. Print it out or download it to your phone.',
      },
      {
        title: 'Visit a Licensed Dispensary',
        description:
          'Bring your digital card, your government-issued photo ID, and proof of Virginia residency to any licensed dispensary. Virginia currently has no recreational retail sales, so a medical card is the only legal way to purchase cannabis in the state.',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your certification expires.',
      },
      {
        title: 'Email Your Current ID to MMJ.com',
        description:
          "Email a clear photo of your valid Virginia driver's license, state ID, or temporary driver's license/state ID to support@MMJ.com.",
      },
      {
        title: 'Receive Your Updated Digital Card',
        description:
          'Your MMJ.com doctor will email your renewed digital medical cannabis card directly to you. Print or download it and continue visiting dispensaries.',
      },
    ],
  },

  wv: {
    name: 'West Virginia',
    registrationUrl: 'https://wv-public.mycomplia.com/',
    fee: '$50 ($0 financial hardship waiver for income below 200% FPL)',
    cardDuration: '1 year',
    new: [
      {
        title: 'Schedule Your MMJ.com Appointment',
        description:
          'Book your telehealth evaluation with an MMJ.com doctor. If approved, your doctor will submit the Patient Certification form directly to the West Virginia Office of Medical Cannabis.',
      },
      {
        title: 'Register on the WV Patient Portal',
        description:
          'Go to the West Virginia patient portal and create your account. Complete the application and upload a passport-quality photo, your photo ID, and proof of West Virginia residency (utility bill within 60 days, tax record, mortgage document, or voter registration).',
        link: 'https://wv-public.mycomplia.com/',
        linkText: 'WV Patient Portal',
      },
      {
        title: 'Pay the $50 Fee (or Request a Waiver)',
        description:
          'Pay the $50 fee online via ACH or mail a check/money order. If your income is below 200% of the federal poverty level, you can request a financial hardship waiver for $0 by providing proof of SNAP, Medicaid, or SSI enrollment.',
      },
      {
        title: 'Download Your Digital Card',
        description:
          'The OMC reviews applications within 30 to 60 days. Once approved, log in and download your digital medical cannabis ID card. West Virginia permits pills, oils, tinctures, topicals, patches, and dry leaf for vaporization only (no smoking, no dispensary-sold edibles).',
        link: 'https://omc.wv.gov/patients/Pages/default.aspx',
        linkText: 'WV OMC Patient Info',
      },
    ],
    renewal: [
      {
        title: 'Schedule Your MMJ.com Renewal Appointment',
        description:
          'Book your renewal evaluation with your MMJ.com doctor before your card expires.',
      },
      {
        title: 'Submit Your Renewal',
        description:
          'Log in to the WV portal and select "Create New Application," then "Patient Renewal." Upload your new certification and any updated documents. Pay the $50 fee or request a hardship waiver.',
        link: 'https://wv-public.mycomplia.com/',
        linkText: 'WV Patient Portal',
      },
      {
        title: 'Download Your Updated Card',
        description:
          'Download your renewed digital card from the portal after approval.',
      },
    ],
  },
};

export default STATE_ONBOARDING_STEPS;
